import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../button/button.element';
import '../icon/icon.element';
import '../menu/menu-item.element';
import '../menu/menu.element';
@customElement('cds-pagination')
export class PaginationElement extends LitElement {
  static styles = css`
    :host {
      border-top: 1px solid
        var(--token-component-pagination-border, light-dark(#d6d6dd, #515157));
      border-bottom: 1px solid
        var(--token-component-pagination-border, light-dark(#d6d6dd, #515157));
      background: var(
        --token-component-pagination-background,
        light-dark(#ffffff, #3d3d42)
      );
      box-sizing: border-box;
      display: flex;
      height: var(--token-global-spacing-s40, 40px);
      flex-direction: row;
      align-items: center;
      align-self: stretch;
      gap: var(--token-global-spacing-s8, 8px);
      padding-left: var(--token-global-spacing-s12, 12px);
      width: 100%;
    }
    .page-items-label {
      flex: 1;
      text-align: left;
    }
    .page-size {
      color: var(--token-component-pagination-text-default, #343438, #ffffff);
      font-size: var(--token-global-font-font-size-xs, 0.875rem); /* 14px */
      font-style: normal;
      font-weight: var(--token-global-font-font-weight-regular-normal, 400);
      line-height: var(--token-global-font-line-height-xxxs, 1rem); /* 16px */
    }
    .page-controls {
      display: flex;
      align-items: center;
      margin-left: var(--token-global-spacing-s12, 12px);

      .page-controls--range {
        color: var(--token-component-pagination-text-default, #343438, #ffffff);
        text-align: center;
        font-family: var(--token-global-font-font-family-brand, 'Nunito Sans');
        font-size: var(--token-global-font-font-size-xs, 0.875rem); /* 14px */
        font-style: normal;
        font-weight: var(--token-global-font-font-weight-regular-normal, 400);
        line-height: var(--token-global-font-line-height-xxxs, 1rem); /* 16px */
      }
    }
  `;

  @property({ type: String })
  itemsLabel: string = 'Items:';

  @property({ type: Number })
  count = 0;

  @property({ type: Number })
  page: number = 1;

  @property({ type: Number })
  pageSize: number = 10;

  @property({ type: String })
  pageSizeLabel: string = 'Page size:';

  @property({ type: Array })
  pageSizes: number[] = [10, 20, 50, 100];

  render() {
    return html`
      <div class="page-items-label">
        ${html`${this.itemsLabel} ${this.count}`}
      </div>
      <div class="page-size">
        <label for="page-size--menu">${this.pageSizeLabel}</label>
        <cds-menu
          icon="chevron-down"
          label=${this.pageSize}
          @select=${this.handlePageSizeChange}
        >
          <label slot="label">${this.pageSize}</label>
          ${this.pageSizes.map(
            (size) => html`
              <cds-menu-item value=${size}>${size}</cds-menu-item>
            `,
          )}
        </cds-menu>
      </div>
      <div class="page-controls">
        <cds-button
          ?disabled=${this.isPreviousDisabled()}
          variant="icon"
          @click=${(e: Event) =>
            this.handlePageChange(e as CustomEvent, 'previous')}
        >
          <cds-icon icon="chevron-left"></cds-icon>
        </cds-button>
        <span class="page-controls--range"
          >${this.toPageRangeLabel(this.page, this.pageSize, this.count)}</span
        >
        <cds-button
          ?disabled=${this.isNextDisabled()}
          variant="icon"
          @click=${(e: Event) =>
            this.handlePageChange(e as CustomEvent, 'next')}
        >
          <cds-icon icon="chevron-right"></cds-icon>
        </cds-button>
      </div>
    `;
  }

  /**
   * Changes the page size and sets the page index so that previously visible elements remain visible.
   *
   * For example, if the page size is 10 and on the second page (items indexed 10-19) then
   * switching so that the page size is 5 will set the third page as the current page so
   * that the 10th item will still be displayed.
   */
  private handlePageSizeChange(e: CustomEvent) {
    const currentStartIndex = (this.page - 1) * this.pageSize;
    const newPageSize = Number(e.detail);
    const newStartIndex = Math.floor(currentStartIndex / newPageSize);

    this.page = newStartIndex + 1;
    this.pageSize = newPageSize;

    this.dispatchEvent(
      new CustomEvent('page-size-change', {
        bubbles: true,
        composed: true,
        detail: { pageSize: this.pageSize, page: this.page },
      }),
    );
  }

  private handlePageChange(
    _e: CustomEvent,
    direction: 'previous' | 'next',
  ): void {
    if (direction === 'previous') {
      this.page--;
    } else {
      this.page++;
    }
    this.dispatchEvent(
      new CustomEvent('page-change', {
        bubbles: true,
        composed: true,
        detail: this.page,
      }),
    );
  }

  private isPreviousDisabled(): boolean {
    return this.page < 2;
  }

  private isNextDisabled(): boolean {
    return this.page * this.pageSize >= this.count;
  }

  private toPageRangeLabel(
    page: number,
    pageSize: number,
    count: number,
  ): string {
    // bad inputs
    if (page <= 0 || pageSize <= 0 || count <= 0) {
      return `0 of 0`;
    }

    const pageIndex = page - 1;
    let startIndex = pageIndex * pageSize;

    // User is switching pageSize and the current page is out of bounds.
    if (startIndex >= count) {
      console.error('[pagination] page is beyond the page size range');
      startIndex = 0;
    }

    const endIndex = Math.min(startIndex + pageSize, count);

    return `${startIndex + 1} - ${endIndex} of ${count}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cds-pagination': PaginationElement;
  }
}
